import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Container,
  Divider,
  Button,
  CircularProgress,
  FormControl,
  Select,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  FileDownload as FileDownloadIcon,
  Inventory as InventoryIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import locationService from '../../services/locationService';
import productService from '../../services/productService';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';

const StockReport = () => {
  const navigate = useNavigate();

  // State for filters
  const [filters, setFilters] = useState({
    locationId: '', // Empty string means "All Locations"
    categoryId: 'all',
    stockStatus: 'all',
  });

  // State for locations and categories
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // State for summary data
  const [summaryData, setSummaryData] = useState({
    lowStock: 0,
    outOfStock: 0,
    inStock: 0,
    totalProducts: 0,
    totalQuantity: 0,
    totalValuePurchase: 0,
    totalValueSelling: 0,
    potentialProfit: 0,
  });

  // State for stock items
  const [stockItems, setStockItems] = useState([]);

  // Add pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Add export state
  const [isExporting, setIsExporting] = useState(false);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Fetch locations and categories on component mount
  useEffect(() => {
    fetchLocations();
    fetchCategories();
  }, []);

  // Auto-load report when locations and categories are loaded
  useEffect(() => {
    if (locations.length > 0 && categories.length > 0 && !loading) {
      handleGenerateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, categories]);

  // Function to fetch locations
  const fetchLocations = async () => {
    try {
      setLocationsLoading(true);
      const response = await locationService.list({ page: 0, size: 1000 });
      const locationsList = response.content || [];

      // Remove duplicates based on name (case-insensitive)
      const uniqueLocations = [];
      const seenNames = new Set();

      locationsList.forEach(location => {
        const normalizedName = location.name?.toLowerCase().trim();
        if (normalizedName && !seenNames.has(normalizedName)) {
          seenNames.add(normalizedName);
          uniqueLocations.push(location);
        }
      });

      setLocations(uniqueLocations);
    } catch (err) {
      console.error('Error fetching locations:', err);
      toast.error('Failed to load locations');
    } finally {
      setLocationsLoading(false);
    }
  };

  // Function to fetch categories
  const fetchCategories = async () => {
    try {
      const response = await productService.getCategories();
      setCategories(response.content || response);
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Failed to load categories');
    }
  };

  // Get status chip color and label
  const getStatusChip = (stockStatus) => {
    if (stockStatus === 'out') {
      return { color: 'error', label: 'Out of Stock', icon: <CancelIcon /> };
    } else if (stockStatus === 'low') {
      return { color: 'warning', label: 'Low Stock', icon: <WarningIcon /> };
    } else {
      return { color: 'success', label: 'In Stock', icon: <CheckCircleIcon /> };
    }
  };

  // Function to handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = {};
      if (filters.locationId !== undefined && filters.locationId !== null && filters.locationId !== '') {
        params.locationId = filters.locationId; // can be numeric string or 'warehouse'
      }
      // If locationId is empty string, don't send it - backend will include all locations
      if (filters.categoryId && filters.categoryId !== 'all') {
        params.categoryId = filters.categoryId;
      }
      if (filters.stockStatus && filters.stockStatus !== 'all') {
        params.stockStatus = filters.stockStatus;
      }

      // Fetch stock report from API
      console.log('Fetching stock report with params:', params);
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.STOCK_REPORT, { params });
      console.log('Stock report response:', response.data);

      // Update summary data
      console.log('Backend summary data:', response.data.summary);
      setSummaryData(response.data.summary || {
        lowStock: 0,
        outOfStock: 0,
        inStock: 0,
        totalProducts: 0,
        totalQuantity: 0,
        totalValuePurchase: 0,
        totalValueSelling: 0,
        potentialProfit: 0,
      });

      // Update stock items
      setStockItems(response.data.stockItems || []);

      // Reset to first page when new data is loaded
      setPage(0);

      toast.success('Stock report generated successfully');
    } catch (err) {
      console.error('Error generating stock report:', err);
      console.error('Error details:', err.response?.data || err.message);

      let errorMessage = 'Failed to generate stock report';
      if (err.response?.status === 404) {
        errorMessage = 'Stock report endpoint not found. Please check the backend configuration.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to access stock reports.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Function to handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);

      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Create a new worksheet
      const ws = {};

      // Set column widths
      ws['!cols'] = [
        { wch: 5 },   // S.No
        { wch: 20 },  // Location
        { wch: 30 },  // Product Name
        { wch: 15 },  // SKU
        { wch: 18 },  // Category
        { wch: 12 },  // Quantity
        { wch: 15 },  // Min Stock Level
        { wch: 18 },  // Purchase Price
        { wch: 18 },  // Selling Price
        { wch: 15 },  // MRP
        { wch: 12 },  // HSN Code
        { wch: 15 },  // Stock Status
        { wch: 20 },  // Value (Purchase)
        { wch: 20 }   // Value (Selling)
      ];

      let currentRow = 1;

      // Add title
      XLSX.utils.sheet_add_aoa(ws, [['Stock Report by Location']], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 16, color: { rgb: "1F4E79" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = [{ s: { c: 0, r: currentRow - 1 }, e: { c: 13, r: currentRow - 1 } }];
      currentRow += 2;

      // Add generation info
      XLSX.utils.sheet_add_aoa(ws, [['Generated on:', new Date().toLocaleString()]], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = { font: { bold: true } };
      currentRow += 1;

      // Add filter info if any filters are applied
      const activeFilters = [];
      if (filters.locationId && filters.locationId !== '') {
        if (filters.locationId === 'warehouse') {
          activeFilters.push('Location: Warehouse');
        } else {
          const location = locations.find(l => l.id.toString() === filters.locationId.toString());
          if (location) activeFilters.push(`Location: ${location.name}`);
        }
      } else {
        activeFilters.push('Location: All Locations');
      }
      if (filters.categoryId) {
        const category = categories.find(c => c.id.toString() === filters.categoryId.toString());
        if (category) activeFilters.push(`Category: ${category.name}`);
      }
      if (filters.stockStatus && filters.stockStatus !== 'all') {
        const statusMap = { 'low': 'Low Stock', 'out': 'Out of Stock', 'in': 'In Stock' };
        activeFilters.push(`Status: ${statusMap[filters.stockStatus]}`);
      }

      if (activeFilters.length > 0) {
        XLSX.utils.sheet_add_aoa(ws, [['Filters:', activeFilters.join(', ')]], { origin: `A${currentRow}` });
        ws[`A${currentRow}`].s = { font: { bold: true } };
        currentRow += 1;
      }
      currentRow += 1;

      // Add summary section
      XLSX.utils.sheet_add_aoa(ws, [['SUMMARY']], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = ws[`!merges`] || [];
      ws[`!merges`].push({ s: { c: 0, r: currentRow - 1 }, e: { c: 13, r: currentRow - 1 } });
      currentRow += 1;

      // Summary data
      console.log('Excel export - summaryData:', summaryData);
      const summaryData2 = [
        ['Total Products:', summaryData.totalProducts],
        ['In Stock:', summaryData.inStock],
        ['Low Stock:', summaryData.lowStock],
        ['Out of Stock:', summaryData.outOfStock],
        ['Total Quantity:', summaryData.totalQuantity],
        ['Total Value (Purchase):', summaryData.totalValuePurchase],
        ['Total Value (Selling):', summaryData.totalValueSelling],
        ['Potential Profit:', summaryData.potentialProfit]
      ];

      summaryData2.forEach((row, idx) => {
        XLSX.utils.sheet_add_aoa(ws, [row], { origin: `A${currentRow + idx}` });
        // Style the labels
        ws[`A${currentRow + idx}`].s = { font: { bold: true } };
        // Style numeric values
        const cellRef = `B${currentRow + idx}`;
        if (ws[cellRef] && typeof row[1] === 'number') {
          if (idx >= 5) { // Currency values (Total Quantity is at index 4, so currency starts from index 5)
            ws[cellRef].s = { numFmt: '"₹"#,##0.00' };
          } else { // Regular numbers
            ws[cellRef].s = { numFmt: '#,##0.##' };
          }
        }
      });
      currentRow += summaryData2.length + 2;

      // Add data section header
      XLSX.utils.sheet_add_aoa(ws, [['DETAILED STOCK REPORT']], { origin: `A${currentRow}` });
      ws[`A${currentRow}`].s = {
        font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center" }
      };
      ws[`!merges`] = ws[`!merges`] || [];
      ws[`!merges`].push({ s: { c: 0, r: currentRow - 1 }, e: { c: 13, r: currentRow - 1 } });
      currentRow += 2;

      // Add table headers
      const headers = [
        'S.No', 'Location', 'Product Name', 'SKU', 'Category',
        'Quantity', 'Min Stock Level', 'Purchase Price', 'Selling Price',
        'MRP', 'HSN Code', 'Stock Status', 'Value (Purchase)', 'Value (Selling)'
      ];

      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${currentRow}` });

      // Style headers
      headers.forEach((header, colIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: currentRow - 1, c: colIdx });
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "2F5597" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      });
      currentRow += 1;

      // Add data rows
      stockItems.forEach((item, idx) => {
        const rowData = [
          idx + 1,
          item.locationName || '',
          item.productName || '',
          item.sku || '',
          item.categoryName || '-',
          `${item.quantity || 0} ${item.uomAbbreviation || ''}`,
          item.minStockLevel || 0,
          item.purchasePrice || 0,
          item.sellingPrice || 0,
          item.mrp || '',
          item.hsnCode || '',
          item.stockStatus === 'out' ? 'Out of Stock' : item.stockStatus === 'low' ? 'Low Stock' : 'In Stock',
          item.valuePurchase || 0,
          item.valueSelling || 0
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: `A${currentRow + idx}` });

        // Style each cell in the row
        rowData.forEach((cellValue, colIdx) => {
          const cellRef = XLSX.utils.encode_cell({ r: currentRow + idx - 1, c: colIdx });
          const cellStyle = {
            border: {
              top: { style: "thin", color: { rgb: "CCCCCC" } },
              bottom: { style: "thin", color: { rgb: "CCCCCC" } },
              left: { style: "thin", color: { rgb: "CCCCCC" } },
              right: { style: "thin", color: { rgb: "CCCCCC" } }
            }
          };

          // Apply specific formatting based on column
          if (colIdx === 0) { // S.No
            cellStyle.alignment = { horizontal: "center" };
          } else if (colIdx === 5) { // Quantity (with UOM)
            cellStyle.alignment = { horizontal: "right" };
          } else if (colIdx === 6) { // Min Stock Level
            cellStyle.numFmt = '#,##0';
            cellStyle.alignment = { horizontal: "right" };
          } else if ([7, 8, 9, 12, 13].includes(colIdx)) { // Price and Value columns (including MRP)
            cellStyle.numFmt = '"₹"#,##0.00';
            cellStyle.alignment = { horizontal: "right" };
          } else if (colIdx === 11) { // Stock Status
            cellStyle.alignment = { horizontal: "center" };
            // Color based on status
            if (cellValue === 'Out of Stock') {
              cellStyle.font = { color: { rgb: "C5504B" } };
            } else if (cellValue === 'Low Stock') {
              cellStyle.font = { color: { rgb: "E36C09" } };
            } else {
              cellStyle.font = { color: { rgb: "70AD47" } };
            }
          } else if (colIdx === 10) { // HSN Code
            cellStyle.alignment = { horizontal: "center" };
          }

          // Alternate row colors
          if (idx % 2 === 1) {
            cellStyle.fill = { fgColor: { rgb: "F8F9FA" } };
          }

          ws[cellRef].s = cellStyle;
        });
      });

      // Set print area and other worksheet properties
      const lastRow = currentRow + stockItems.length - 1;
      ws['!ref'] = `A1:N${lastRow}`;
      ws['!autofilter'] = { ref: `A${currentRow - 1}:N${lastRow}` };

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Stock Report');

      // Generate and download the file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Stock report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export stock report');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ fontSize: '0.92rem' }}>
        <Box sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InventoryIcon sx={{ fontSize: 32, mr: 1, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" sx={{ fontSize: '1.3rem' }}>
              Stock Report by Location
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* Action Buttons Row */}
          <Grid container justifyContent="flex-end" spacing={2} sx={{ mb: 2 }}>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate('/reports/stock-matrix')}
                startIcon={<GridViewIcon />}
                sx={{
                  minWidth: 200,
                  height: '38px',
                  fontWeight: 600,
                  fontSize: '0.92rem',
                  background: 'linear-gradient(45deg, #9c27b0 30%, #e91e63 90%)',
                  boxShadow: '0 3px 5px 2px rgba(156, 39, 176, .3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #7b1fa2 30%, #c2185b 90%)',
                  }
                }}
              >
                View Stock Matrix
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportToExcel}
                disabled={isExporting || stockItems.length === 0}
                startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                sx={{ minWidth: 160, height: '38px', fontWeight: 600, fontSize: '0.92rem' }}
              >
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </Grid>
          </Grid>

          {/* Search and Filter Section */}
          <Paper sx={{ p: 2, mb: 2, fontSize: '0.92rem' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Select
                    value={filters.locationId}
                    onChange={(e) => handleFilterChange('locationId', e.target.value)}
                    disabled={locationsLoading}
                    displayEmpty
                    renderValue={(value) => {
                      if (value === '' || value === null || value === undefined) {
                        return 'All Locations';
                      }
                      if (value === 'warehouse') {
                        return 'Warehouse';
                      }
                      const selectedLocation = locations.find(loc => loc.id === value);
                      return selectedLocation ? selectedLocation.name : 'All Locations';
                    }}
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {/* Only show "Warehouse" for old companies (where "Main Location" doesn't exist) */}
                    {!locations.some(loc => loc.name?.toLowerCase().trim() === 'main location') && (
                      <MenuItem value="warehouse">Warehouse</MenuItem>
                    )}
                    {locationsLoading ? (
                      <MenuItem disabled>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} />
                          <span>Loading locations...</span>
                        </Box>
                      </MenuItem>
                    ) : (
                      locations
                        .filter(location => {
                          // Filter out "Warehouse" to avoid duplicates with hardcoded option
                          // For new companies, "Main Location" is shown from the locations list
                          const name = location.name?.toLowerCase().trim();
                          return name !== 'warehouse';
                        })
                        .map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.name}
                          </MenuItem>
                        ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  variant="outlined"
                  value={filters.categoryId}
                  onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                  displayEmpty
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 300, overflowY: 'auto' }
                      },
                      disableScrollLock: true
                    }
                  }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Stock Status"
                  variant="outlined"
                  value={filters.stockStatus}
                  onChange={(e) => handleFilterChange('stockStatus', e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="low">Low Stock</MenuItem>
                  <MenuItem value="out">Out of Stock</MenuItem>
                  <MenuItem value="in">In Stock</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleGenerateReport}
                  disabled={loading}
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Summary Cards - Row 1 */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Total Products Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Total Products
                      </Typography>
                      <Typography variant="h6" component="div" color="primary.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.totalProducts}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'primary.main' }}>
                      <InventoryIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* In-Stock Products Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        In Stock
                      </Typography>
                      <Typography variant="h6" component="div" color="success.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.inStock}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'success.main' }}>
                      <CheckCircleIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Low Stock Items Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Low Stock
                      </Typography>
                      <Typography variant="h6" component="div" color="warning.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.lowStock}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'warning.main' }}>
                      <WarningIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Out of Stock Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Out of Stock
                      </Typography>
                      <Typography variant="h6" component="div" color="error.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.outOfStock}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'error.main' }}>
                      <CancelIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Summary Cards - Row 2 */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Total Quantity Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Total Quantity
                      </Typography>
                      <Typography variant="h6" component="div" color="primary.main" sx={{ fontSize: '1.1rem' }}>
                        {summaryData.totalQuantity}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'primary.main' }}>
                      <InventoryIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Purchase Price Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Total Purchase Price
                      </Typography>
                      <Typography variant="h6" component="div" color="warning.main" sx={{ fontSize: '1.05rem' }}>
                        {formatCurrency(summaryData.totalValuePurchase)}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'warning.main' }}>
                      <InventoryIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Selling Price Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Total Selling Price
                      </Typography>
                      <Typography variant="h6" component="div" color="success.main" sx={{ fontSize: '1.05rem' }}>
                        {formatCurrency(summaryData.totalValueSelling)}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'success.main' }}>
                      <InventoryIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Profit Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', minHeight: '90px' }}>
                <CardContent sx={{ p: 1.5, fontSize: '0.98rem' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2" component="div" gutterBottom sx={{ fontSize: '0.98rem' }}>
                        Total Profit
                      </Typography>
                      <Typography variant="h6" component="div" color="info.main" sx={{ fontSize: '1.05rem' }}>
                        {formatCurrency(summaryData.potentialProfit)}
                      </Typography>
                    </Box>
                    <Box sx={{ color: 'info.main' }}>
                      <InventoryIcon sx={{ fontSize: 26 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Stock Table */}
          <TableContainer component={Paper} sx={{ fontSize: '0.92rem' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Location</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Min Level</TableCell>
                  <TableCell align="right">Purchase Price</TableCell>
                  <TableCell align="right">Selling Price</TableCell>
                  <TableCell>Stock Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : stockItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No stock items found. Click "Generate Report" to load data.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  // Apply pagination to stock items
                  stockItems
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => {
                      const status = getStatusChip(item.stockStatus);
                      return (
                        <TableRow key={`${item.locationProductId}-${index}`}>
                          <TableCell>{item.locationName}</TableCell>
                          <TableCell>{item.productName}</TableCell>
                          <TableCell>{item.sku}</TableCell>
                          <TableCell>{item.categoryName || '-'}</TableCell>
                          <TableCell align="right">{item.quantity} {item.uomAbbreviation}</TableCell>
                          <TableCell align="right">{item.minStockLevel}</TableCell>
                          <TableCell align="right">{formatCurrency(item.purchasePrice)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.sellingPrice)}</TableCell>
                          <TableCell>
                            <Chip
                              label={status.label}
                              color={status.color}
                              size="small"
                              icon={status.icon}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
            {!loading && stockItems.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={stockItems.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </TableContainer>
        </Box>
      </Container>
    </Layout>
  );
};

export default StockReport;


